import { alpha } from "@mui/material/styles";
import { type FC, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePickOpenShift } from "@/components/OpenShiftCard/hooks";
import { useAuth } from "@/contexts/AuthProvider";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { INK_BLACK } from "@/theme/tokens";
import SystemDialog from "@/ui/Popups/Dialog";
import SystemButton from "@/ui/SystemButton/SystemButton";

type Props = {
	requestIdsToPick: string[];
	onSuccess?: () => void;
};

export const PickRequestsFAB: FC<Props> = ({ requestIdsToPick, onSuccess }) => {
	const { t } = useTranslation();
	const { mutateAsync: pickShiftAsync, isPending: isPickingSingle } = usePickOpenShift({ refreshUponSuccess: true });
	const [renderApproveSelection, setRenderApproveSelection] = useState(false);
	const { chosenDepartment } = useChosenDepartment();
	const { showSnackbar } = useSnackbar();
	const { userInfo } = useAuth();
	const nurseId = userInfo?.nurses_info?.at(0)?.nurse.id;
	const hasRequestIdsToPick = requestIdsToPick.length > 0;

	const cancelApproveSelection = () => {
		setRenderApproveSelection(false);
	};

	const performPostPickActions = async () => {
		setRenderApproveSelection(false);
		if (nurseId && hasRequestIdsToPick && !isPickingSingle) {
			const promises = requestIdsToPick.map((requestId) =>
				pickShiftAsync({
					openShiftId: requestId,
					departmentId: chosenDepartment.department_id,
					nurseId,
				}).catch(() => {
					// swallow error, handled in mutation
				}),
			);
			await Promise.all(promises);
			showSnackbar(t("success.picked.open.shifts"), "success");
		}
		onSuccess?.();
	};

	const handleClick = async () => {
		setRenderApproveSelection(true);
	};

	return (
		<Fragment>
			<SystemButton
				loading={isPickingSingle}
				sx={{
					position: "fixed",
					bottom: "20%",
					right: 8,
					zIndex: 1000,
					display: hasRequestIdsToPick ? "flex" : "none",
					boxShadow: `0px 2px 8px ${alpha(INK_BLACK, 0.5)}`,
					width: "fit-content",
				}}
				onClick={handleClick}
			>
				{t("pick")} {requestIdsToPick.length}
			</SystemButton>
			{renderApproveSelection && (
				<SystemDialog
					open={renderApproveSelection}
					onClose={cancelApproveSelection}
					title={t("confirm.pick.requests")}
					body={t("pick.selected.shift.requests")}
					onConfirm={performPostPickActions}
				/>
			)}
		</Fragment>
	);
};
